import React from 'react';
import { Box, Typography, Grid, Button, alpha } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import CustomerSelect from '../../features/invoices/components/CustomerSelect';

/**
 * DocumentHeaderSection Component
 * Displays company info (left) and customer info (right) side by side
 * Reusable across Delivery Notes, Sale Orders, and Invoices
 */
const DocumentHeaderSection = ({
  storeInfo,
  selectedCustomer,
  onCustomerChange,
  onCustomerClear,
  onAddCustomerClick,
  loadCustomerOptions,
  disabled = false,
  onEditCustomerClick,
  documentLabel = '',
  accentColor = '#667eea',
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ position: 'relative', mb: 4, overflow: 'hidden' }}>

      {/* WATERMARK: absolute overlay, shifted left, 40° angle, fills gap between sections */}
      {documentLabel && (
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            position: 'absolute',
            left: '32%',
            top: '50%',
            transform: 'translate(-50%, -50%) rotate(-20deg)',
            fontSize: '3.7rem',
            fontWeight: 900,
            color: accentColor,
            opacity: 0.15,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            userSelect: 'none',
            pointerEvents: 'none',
            lineHeight: 1.15,
            zIndex: 0,
          }}
        >
          {documentLabel.split(' ').map((word, i) => (
            <span key={i}>{word}</span>
          ))}
        </Box>
      )}

      <Grid container spacing={3} sx={{ alignItems: 'stretch', position: 'relative', zIndex: 1 }}>
        {/* LEFT: Company Information */}
        <Grid item xs={12} md={6}>
          <Typography 
            variant="overline" 
            fontWeight="600" 
            color={accentColor}
            sx={{ fontSize: '0.75rem', letterSpacing: '1px', mb: 1, display: 'block' }}
          >
            {t("from") || "FROM"}
          </Typography>
          <Typography variant="h4" fontWeight="700" color="#2c3e50" sx={{ mb: 1.5 }}>
            {storeInfo?.name || t("company_name") || "Your Company Name"}
          </Typography>
          <Typography variant="body2" color="#555" sx={{ lineHeight: 2 }}>
            {storeInfo?.vat && <><strong>{t("vat_number")}:</strong> {storeInfo.vat}<br /></>}
            {storeInfo?.address && <>{storeInfo.address}<br /></>}
            {storeInfo?.postCode && storeInfo?.city && <>{storeInfo.postCode} {storeInfo.city}{storeInfo?.country ? ', ' + storeInfo.country : ''}</>}
          </Typography>
        </Grid>

      {/* RIGHT: Customer Information */}
      <Grid item xs={12} md={6}>
        <Typography 
          variant="overline" 
          fontWeight="600" 
          color={accentColor}
          sx={{ fontSize: '0.75rem', letterSpacing: '1px', mb: 1, display: 'block' }}
        >
          {t("ship_to") || "SHIP TO"}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>

          <Box sx={{ flex: 1 }}>
            <CustomerSelect
              value={selectedCustomer}
              onChange={onCustomerChange}
              loadOptions={loadCustomerOptions}
              inline={true}
              isDisabled={disabled}
              onEditClick={onEditCustomerClick}
            />
          </Box>
          {onAddCustomerClick && !selectedCustomer && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddCustomerClick}
              disabled={disabled}
              sx={{
                borderRadius: '6px',
                px: 2,
                height: '40px',
                whiteSpace: 'nowrap',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderColor: '#764ba2',
                  bgcolor: alpha('#667eea', 0.05)
                }
              }}
            >
              {t("add_new")}
            </Button>
          )}
        </Box>
      </Grid>
    </Grid>
    </Box>
  );
};

export default DocumentHeaderSection;
